import { motion } from 'framer-motion';
import {
  ArrowRight, ArrowUpRight, CheckCircle,
  Handshake, Scale, ShieldCheck, Sparkles
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Header } from './HomePageLab';

const PRINCIPES = [
  {
    n: '01',
    titre: 'Une responsabilité partagée.',
    desc: "PESACORE ne cherche pas des revendeurs. Elle cherche des acteurs capables de déployer la méthodologie avec rigueur — et d'en assumer les résultats sur le terrain. Ce que vous faites avec la méthode nous engage autant que vous.",
  },
  {
    n: '02',
    titre: 'La cohérence prime sur la croissance.',
    desc: "Chaque partenaire engage sa réputation dans chaque déploiement. C'est la raison pour laquelle un processus de qualification existe — non pas pour filtrer, mais pour s'assurer que ce qui est promis aux bénéficiaires est effectivement produit.",
  },
  {
    n: '03',
    titre: "L'accès est sélectif. L'accompagnement est total.",
    desc: "Les partenaires engagés disposent d'un accès complet à la méthodologie, aux ressources pédagogiques et à un interlocuteur dédié. Il ne s'agit pas d'un portail en libre-service, mais d'une relation de travail construite dans la durée.",
  },
];

const SOLUTIONS = [
  { icon: <Scale size={30} />, titre: 'Alignement sur la méthodologie', desc: "Avant tout engagement contractuel, un échange approfondi. L'objectif est de vérifier que les intentions sont compatibles et que le partenaire comprend précisément ce que la méthodologie produit — ainsi que les exigences qu'elle implique.", detail: 'Qualification en 2 échanges' },
  { icon: <ShieldCheck size={30} />, titre: 'Accès complet aux ressources', desc: "Protocoles, simulations configurables, analytics décisionnels, Panel Grande École — les mêmes outils qu'en interne, sans version allégée. Un accès réel aux éléments qui produisent les résultats.", detail: 'Espace partenaire dédié' },
  { icon: <Handshake size={30} />, titre: 'Interlocuteur dédié', desc: "Un point de contact unique, impliqué et réactif. La relation ne s'arrête pas à la signature — elle commence là. Chaque déploiement est suivi, chaque résultat documenté, chaque difficulté traitée dans le cadre de la collaboration.", detail: 'Accompagnement continu' },
  { icon: <Sparkles size={30} />, titre: 'Co-construction possible', desc: "Adaptation de contenus à vos contextes spécifiques, création de simulations sur mesure, intégration dans vos parcours existants. La méthodologie est suffisamment robuste pour supporter l'adaptation — à condition que le cadre logique soit préservé.", detail: 'Sur qualification' },
];

const ETAPES = [
  { n: '01', titre: 'Vous prenez contact', detail: "Un message décrivant votre activité, votre contexte et les raisons pour lesquelles ce rapprochement vous semble pertinent. Pas de dossier formel requis à ce stade.", duree: 'Maintenant' },
  { n: '02', titre: 'Premier échange', detail: "Un entretien d'environ trente minutes. L'objectif n'est pas de convaincre, mais d'établir si les intentions sont convergentes. La réponse est donnée rapidement, dans un sens ou dans l'autre.", duree: 'J+5' },
  { n: '03', titre: 'Accès pilote encadré', detail: "Si l'alignement est avéré, un premier déploiement guidé est organisé. C'est sur le terrain que la méthodologie se valide — pas en amont.", duree: 'J+15' },
  { n: '04', titre: 'Collaboration active', detail: "Accès complet à la plateforme, interlocuteur dédié, ressources à jour. La relation de travail s'installe dans la durée, avec des points de suivi réguliers.", duree: 'J+30' },
];

const ClauseLine = ({ n, delay = 0 }) => (
  <motion.div
    initial={{ scaleX: 0, opacity: 0 }}
    whileInView={{ scaleX: 1, opacity: 1 }}
    viewport={{ once: true }}
    transition={{ duration: 1.2, delay, ease: 'easeOut' }}
    className="flex items-center gap-3 mb-6"
    style={{ transformOrigin: 'left' }}
  >
    <span className="text-[8px] font-mono text-amber-200 uppercase tracking-[0.3em] shrink-0">Art. {n}</span>
    <div className="h-px flex-1 bg-gradient-to-r from-amber-700/20 to-transparent" />
  </motion.div>
);

const MirrorLine = () => (
  <div className="hidden lg:flex flex-col items-center justify-center relative">
    <motion.div
      initial={{ scaleY: 0 }}
      animate={{ scaleY: 1 }}
      transition={{ duration: 1.5, delay: 0.8, ease: 'easeOut' }}
      className="w-px bg-gradient-to-b from-transparent via-amber-600/40 to-transparent"
      style={{ height: '320px', transformOrigin: 'top' }}
    />
    <motion.div
      className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-amber-500"
      animate={{
        boxShadow: ['0 0 0px rgba(245,158,11,0)', '0 0 16px rgba(245,158,11,0.6)', '0 0 0px rgba(245,158,11,0)'],
      }}
      transition={{ duration: 2.4, repeat: Infinity }}
    />
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.4 }}
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 mt-6 px-2 py-1 bg-[#020617]"
      style={{ border: '1px solid rgba(217,119,6,0.2)' }}
    >
      <span className="text-[7px] font-mono text-amber-600/50 uppercase tracking-[0.3em] whitespace-nowrap">↕ Échange</span>
    </motion.div>
  </div>
);

export default function LandingPagePartner() {
  const navigate = useNavigate();
  const goContact = () => navigate('/contact?from=partenariat&sujet=partenariat');
  const goPartnersLink = () => navigate('/partners-link');

  return (
    <div className="min-h-screen bg-[#020617] text-stone-200 relative overflow-hidden">

      {/* Fond diagonales */}
      <div className="fixed inset-0 pointer-events-none" style={{
        backgroundImage: `repeating-linear-gradient(
          -45deg,
          transparent,
          transparent 48px,
          rgba(217,119,6,0.018) 48px,
          rgba(217,119,6,0.018) 49px
        )`,
      }} />
      <div className="fixed inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_50%_25%,rgba(217,119,6,0.06),transparent_55%)]" />
      <div className="fixed inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_50%_80%,rgba(120,113,108,0.04),transparent_55%)]" />

      <Header />

      {/* ══ HERO ══
          MOBILE FIX : min-h réduit, padding ajusté */}
      <section className="relative pt-24 sm:pt-28 pb-12 sm:pb-20 px-4 sm:px-6 min-h-[auto] sm:min-h-[85vh] flex items-start sm:items-center">
        <div className="max-w-7xl mx-auto w-full">

          {/* En-tête de contrat — MOBILE FIX : flex-col sur très petit écran */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7 }}
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-8 sm:mb-12 pb-4"
            style={{ borderBottom: '1px solid rgba(217,119,6,0.08)' }}
          >
            <div className="flex items-center gap-3">
              <div className="h-px w-6 bg-amber-700/30" />
              <span className="text-[9px] font-mono text-amber-300/50 uppercase tracking-[0.25em] sm:tracking-[0.35em]">
                Convention de partenariat
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-amber-500/40" />
              <span className="text-[9px] font-mono text-amber-200 uppercase tracking-[0.25em] hidden sm:block">Réf. PAR-CONV-2026</span>
            </div>
          </motion.div>

          {/* Layout miroir
              MOBILE FIX : grid-cols-1, MirrorLine masquée sur mobile */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-8 sm:gap-8 items-start lg:items-center">

            {/* PARTIE A */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="text-[9px] font-mono text-amber-200 uppercase tracking-[0.3em] mb-4">Partie A · PESACORE</div>

              {/* MOBILE FIX : clamp sur H1 */}
              <h1 className="font-black uppercase tracking-tight leading-[0.92] text-white mb-5 sm:mb-6"
                style={{ fontSize: 'clamp(2rem, 8vw, 4rem)', fontFamily: 'Georgia, serif' }}>
                Construire<br />
                <span className="italic font-normal" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  ensemble.
                </span>
              </h1>

              <p className="text-stone-200 leading-relaxed mb-5 text-sm max-w-sm"
                style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
                PESACORE n'a pas de programme partenaire standardisé. Elle noue des{' '}
                <span className="text-white not-italic font-semibold">collaborations</span> — choisies, qualifiées, et construites dans la durée avec des acteurs qui partagent les mêmes exigences de résultat.
              </p>

              <div className="space-y-2 mb-6">
                <div className="text-[9px] font-mono text-amber-200 uppercase tracking-[0.3em] mb-3">Ce que PESACORE apporte</div>
                {[
                  'Protocole complet · 15 ans de terrain validé',
                  'Simulations configurables · Analytics décisionnels',
                  'Interlocuteur dédié · Accompagnement total',
                  'Résultats mesurables documentés',
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.7 + i * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-px h-3 bg-amber-700/30 shrink-0" />
                    <span className="text-xs text-stone-100" style={{ fontFamily: 'monospace' }}>{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Ligne miroir centrale — masquée sur mobile */}
            <div className="hidden lg:block">
              <MirrorLine />
            </div>

            {/* PARTIE B */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="text-[9px] font-mono text-stone-200 uppercase tracking-[0.3em] mb-4">Partie B · Vous</div>

              {/* Carte positionnement */}
              <div className="p-5 sm:p-6 mb-5 sm:mb-6" style={{ border: '1px solid rgba(217,119,6,0.1)', background: 'rgba(2,6,23,0.8)' }}>
                <div className="text-[9px] font-mono text-stone-200 uppercase tracking-[0.25em] mb-4 sm:mb-5">Ce partenariat n'est pas — et ce qu'il est</div>
                <div className="space-y-4">
                  {[
                    { non: "Un réseau d'affiliés rémunérés à la performance", oui: "Une relation de travail qualifiée, fondée sur les résultats produits" },
                    { non: "Un portail de ressources en libre-service", oui: "Un accès structuré, accompagné par un interlocuteur dédié" },
                    { non: "Un modèle de revente passive de licences", oui: "Un engagement conjoint sur la qualité des déploiements" },
                  ].map((item, i) => (
                    <div key={i} className="space-y-1.5 pb-3 last:pb-0"
                      style={{ borderBottom: i < 2 ? '1px solid rgba(41,37,36,0.6)' : 'none' }}>
                      <div className="flex items-start gap-2">
                        <span className="text-stone-300 text-xs shrink-0 mt-0.5">✕</span>
                        <span className="text-stone-200 text-xs leading-snug" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>{item.non}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-amber-600/70 text-xs shrink-0 mt-0.5">→</span>
                        <span className="text-stone-300 text-xs leading-snug">{item.oui}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[9px] font-mono text-stone-300 italic mt-4 text-right">
                  "Un partenaire PESACORE produit des transformations — et en rend compte."
                </p>
              </div>

              {/* CTAs — MOBILE FIX : flex-col par défaut, row sur sm+ */}
              <div className="flex flex-col sm:flex-row gap-3 mb-5">
                <button
                  onClick={goContact}
                  className="group relative flex items-center justify-center gap-2 px-6 py-3 overflow-hidden w-full sm:flex-1"
                  style={{ background: 'rgba(217,119,6,0.08)', border: '1.5px solid rgba(217,119,6,0.3)' }}
                >
                  <motion.span
                    className="absolute inset-0"
                    style={{ background: 'linear-gradient(90deg, transparent, rgba(217,119,6,0.06), transparent)' }}
                    animate={{ x: ['-100%', '100%'] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'linear' }}
                  />
                  <span className="text-xs font-black uppercase tracking-wider text-white relative z-10">Engager la conversation</span>
                  <ArrowRight size={13} className="text-amber-500 group-hover:translate-x-1 transition-transform relative z-10" />
                </button>

                <motion.button
                  onClick={goPartnersLink}
                  className="relative flex items-center justify-center gap-2.5 px-5 py-3 overflow-hidden group w-full sm:w-auto"
                  style={{ background: 'rgba(99,102,241,0.08)', border: '1.5px solid rgba(99,102,241,0.35)' }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <motion.span
                    className="absolute inset-0 rounded-none"
                    style={{ background: 'rgba(99,102,241,0.12)' }}
                    animate={{ opacity: [0, 0.6, 0] }}
                    transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
                  />
                  <motion.span
                    className="absolute inset-0 pointer-events-none"
                    style={{ border: '1.5px solid rgba(129,140,248,0.6)' }}
                    animate={{ opacity: [0, 1, 0] }}
                    transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
                  />
                  <motion.span
                    className="absolute inset-0"
                    style={{ background: 'linear-gradient(90deg, transparent, rgba(129,140,248,0.08), transparent)' }}
                    animate={{ x: ['-100%', '100%'] }}
                    transition={{ duration: 2.0, repeat: Infinity, ease: 'linear', repeatDelay: 0.8 }}
                  />
                  <motion.span
                    className="relative z-10 w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ background: '#818cf8' }}
                    animate={{ opacity: [1, 0.2, 1], scale: [1, 0.7, 1] }}
                    transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                  />
                  <span className="text-xs font-mono uppercase tracking-widest relative z-10"
                    style={{ color: '#a5b4fc' }}>
                    Moteur partenariat
                  </span>
                  <motion.span
                    className="relative z-10 text-[10px] font-mono"
                    style={{ color: '#6366f1' }}
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 1.4, repeat: Infinity }}
                  >
                    ↗
                  </motion.span>
                </motion.button>
              </div>

              {/* MOBILE FIX : flex-wrap pour éviter débordement */}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-stone-300 font-mono">
                <div className="flex items-center gap-1.5"><CheckCircle size={12} className="text-amber-300/50" /><span>Sans dossier préalable</span></div>
                <div className="flex items-center gap-1.5"><CheckCircle size={12} className="text-amber-300/50" /><span>Réponse sous 48h</span></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ PRINCIPES ══ */}
      <section className="py-12 sm:py-16 px-4 sm:px-6" style={{ borderTop: '1px solid rgba(217,119,6,0.06)', borderBottom: '1px solid rgba(217,119,6,0.06)', background: 'rgba(2,6,23,0.7)' }}>
        <div className="max-w-5xl mx-auto">
          <ClauseLine n="I" delay={0} />
          {/* MOBILE FIX : clamp h2 */}
          <h2 className="font-black uppercase tracking-tight text-white mb-8 sm:mb-10"
            style={{ fontSize: 'clamp(1.3rem, 5vw, 1.5rem)' }}>
            Ce que nous entendons par{' '}
            <span style={{ background: 'linear-gradient(90deg, #f59e0b, #d97706)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              partenariat
            </span>
          </h2>
          <div className="space-y-0" style={{ border: '1px solid rgba(217,119,6,0.07)' }}>
            {PRINCIPES.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
                /* MOBILE FIX : grid-cols-1 par défaut → md:grid-cols-[auto_1fr] */
                className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-2 sm:gap-6 p-5 sm:p-7 group hover:bg-amber-700/3 transition-all"
                style={{ borderBottom: i < PRINCIPES.length - 1 ? '1px solid rgba(217,119,6,0.06)' : 'none' }}
              >
                <div className="shrink-0">
                  <div className="text-[9px] font-mono text-amber-300/35 uppercase tracking-[0.3em]">{p.n} ·</div>
                </div>
                <div>
                  <h3 className="text-base font-black uppercase tracking-tight text-white mb-2">{p.titre}</h3>
                  <p className="text-stone-100 text-sm leading-relaxed" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>{p.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CE QUE VOUS RECEVEZ ══ */}
      <section className="py-12 sm:py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <ClauseLine n="II" delay={0} />
          {/* MOBILE FIX : clamp h2 */}
          <h2 className="font-black uppercase tracking-tight text-white mb-2 sm:mb-3"
            style={{ fontSize: 'clamp(1.3rem, 5vw, 1.5rem)' }}>
            Ce que vous{' '}
            <span style={{ background: 'linear-gradient(90deg, #f59e0b, #d97706)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              recevez
            </span>
          </h2>
          <p className="text-stone-200 text-sm mb-8 sm:mb-10" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
            Un accès complet à la méthodologie et aux ressources qui la rendent opérationnelle — les mêmes que ceux utilisés en interne.
          </p>
          {/* MOBILE FIX : grid-cols-1 → md:grid-cols-2 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            {SOLUTIONS.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-5 sm:p-7 bg-[#020617] group hover:bg-amber-700/3 transition-all"
                style={{
                  borderTop: '1px solid rgba(217,119,6,0.07)',
                  borderRight: i % 2 === 0 ? '1px solid rgba(217,119,6,0.07)' : 'none',
                }}
              >
                <div className="text-amber-600/50 mb-4 group-hover:text-amber-600/80 transition-colors">{s.icon}</div>
                <h3 className="text-sm font-black uppercase tracking-tight text-white mb-2">{s.titre}</h3>
                <p className="text-stone-100 text-xs leading-relaxed mb-4" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>{s.desc}</p>
                <span className="text-[9px] font-mono text-amber-300/50 uppercase tracking-wider pb-0.5"
                  style={{ borderBottom: '1px solid rgba(217,119,6,0.15)' }}>{s.detail}</span>
              </motion.div>
            ))}
            <div className="col-span-1 md:col-span-2" style={{ borderTop: '1px solid rgba(217,119,6,0.07)' }} />
          </div>
        </div>
      </section>

      {/* ══ PROCESSUS ══ */}
      <section className="py-12 sm:py-16 px-4 sm:px-6" style={{ borderTop: '1px solid rgba(217,119,6,0.06)', borderBottom: '1px solid rgba(217,119,6,0.06)', background: 'rgba(2,6,23,0.7)' }}>
        <div className="max-w-5xl mx-auto">
          <ClauseLine n="III" delay={0} />
          {/* MOBILE FIX : clamp h2 */}
          <h2 className="font-black uppercase tracking-tight text-white mb-2"
            style={{ fontSize: 'clamp(1.3rem, 5vw, 1.5rem)' }}>
            Comment ça{' '}
            <span style={{ background: 'linear-gradient(90deg, #f59e0b, #d97706)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              se passe
            </span>
          </h2>
          <p className="text-stone-200 text-sm mb-8 sm:mb-10" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
            Un processus court. Une décision prise conjointement, à chaque étape.
          </p>

          <div className="relative">
            {/* Ligne de progression — masquée sur mobile */}
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.5, delay: 0.3, ease: 'easeOut' }}
              className="absolute top-[22px] left-[24px] right-[24px] h-px bg-gradient-to-r from-amber-700/30 via-amber-600/20 to-amber-700/10 hidden md:block"
              style={{ transformOrigin: 'left' }}
            />

            {/* MOBILE FIX : grid-cols-1 → md:grid-cols-4, gap réduit sur mobile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-4">
              {ETAPES.map((e, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + i * 0.15 }}
                  className="relative"
                >
                  {/* Point de la timeline — masqué sur mobile */}
                  <div className="w-3 h-3 bg-[#020617] mb-4 relative z-10 hidden md:flex items-center justify-center"
                    style={{ border: '1px solid rgba(217,119,6,0.25)' }}>
                    <div className="w-1 h-1 bg-amber-600/50" />
                  </div>

                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[9px] font-mono text-amber-200 tracking-[0.25em]">{e.n} ·</span>
                    <span className="text-[8px] font-mono text-stone-300 uppercase tracking-wider px-1.5 py-0.5"
                      style={{ border: '1px solid rgba(41,37,36,0.6)' }}>{e.duree}</span>
                  </div>
                  <h3 className="text-sm font-black uppercase tracking-tight text-white mb-2">{e.titre}</h3>
                  <p className="text-stone-200 text-xs leading-relaxed" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>{e.detail}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ CONDITIONS ══ */}
      <section className="py-12 sm:py-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <ClauseLine n="IV" delay={0} />
          {/* MOBILE FIX : clamp h2 */}
          <h2 className="font-black uppercase tracking-tight text-white mb-2"
            style={{ fontSize: 'clamp(1.3rem, 5vw, 1.5rem)' }}>
            Conditions{' '}
            <span style={{ background: 'linear-gradient(90deg, #f59e0b, #d97706)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              minimales
            </span>
          </h2>
          <p className="text-stone-200 text-sm mb-6 sm:mb-8" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
            Ces conditions ne sont pas des contraintes formelles. Elles définissent le cadre dans lequel une collaboration sérieuse est possible.
          </p>
          <div style={{ border: '1px solid rgba(217,119,6,0.07)' }}>
            {[
              { label: 'Compréhension de la méthodologie', detail: 'Validée lors du premier échange' },
              { label: 'Présence terrain active', detail: 'Un rôle de déployeur et de médiation' },
              { label: 'Alignement éthique', detail: 'Anti-addictif · Impact réel · RGPD' },
              { label: 'Engagement dans la durée', detail: 'Collaboration tangible (à partir de 6 mois)' },
            ].map((c, i, arr) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4 px-4 sm:px-7 py-4 group hover:bg-amber-700/3 transition-all"
                style={{ borderBottom: i < arr.length - 1 ? '1px solid rgba(217,119,6,0.06)' : 'none' }}
              >
                <div className="flex items-center gap-3">
                  <CheckCircle size={13} className="text-amber-200 shrink-0" />
                  <span className="text-stone-300 text-sm">{c.label}</span>
                </div>
                <span className="text-[9px] font-mono text-stone-300 uppercase tracking-wider pl-6 sm:pl-0">{c.detail}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CTA FINAL ══ */}
      <section className="py-12 sm:py-16 px-4 sm:px-6" style={{ borderTop: '1px solid rgba(217,119,6,0.06)' }}>
        <div className="max-w-4xl mx-auto">
          <div className="relative p-8 sm:p-14 overflow-hidden" style={{
            background: 'rgba(2,6,23,0.9)',
            border: '1px solid rgba(217,119,6,0.08)',
          }}>
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(217,119,6,0.05),transparent_60%)] pointer-events-none" />
            <div className="absolute inset-0 pointer-events-none" style={{
              backgroundImage: `repeating-linear-gradient(-45deg, transparent, transparent 30px, rgba(217,119,6,0.02) 30px, rgba(217,119,6,0.02) 31px)`,
            }} />

            <span className="absolute top-0 left-0 w-6 h-6 border-t border-l border-amber-700/20" />
            <span className="absolute bottom-0 right-0 w-6 h-6 border-b border-r border-amber-700/15" />

            <div className="flex items-center gap-3 mb-5 sm:mb-6 relative z-10 flex-wrap">
              <div className="h-px w-8 bg-amber-700/25" />
              <span className="text-[9px] font-mono text-amber-200 uppercase tracking-[0.3em]">Art. V · Prise de contact</span>
              <div className="h-px flex-1 bg-amber-700/8 hidden sm:block" />
            </div>

            <p className="font-mono text-[10px] uppercase tracking-widest text-amber-300/50 mb-4 relative z-10">
              Ce n'est pas une candidature. C'est une conversation.
            </p>
            {/* MOBILE FIX : clamp h2 */}
            <h2 className="font-black uppercase tracking-tight mb-4 relative z-10 text-white"
              style={{ fontSize: 'clamp(1.4rem, 5vw, 1.875rem)' }}>
              Si ça a du sens,{' '}
              <span className="italic font-normal" style={{ fontFamily: 'Georgia, serif', background: 'linear-gradient(90deg, #f59e0b, #d97706)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                parlons-en.
              </span>
            </h2>
            <p className="text-stone-100 mb-8 sm:mb-10 relative z-10 max-w-lg leading-relaxed text-sm"
              style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
              Un message décrivant votre activité et ce que vous envisagez suffit pour initier l'échange. Nous lisons chaque prise de contact avec attention et répondons sous 48 heures.
            </p>

            {/* MOBILE FIX : boutons full-width sur mobile */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 relative z-10">
              <button
                onClick={goContact}
                className="group relative flex items-center justify-center gap-3 px-8 py-3.5 overflow-hidden w-full sm:w-auto"
                style={{ background: 'rgba(217,119,6,0.08)', border: '1.5px solid rgba(217,119,6,0.3)' }}
              >
                <motion.span
                  className="absolute inset-0"
                  style={{ background: 'linear-gradient(90deg, transparent, rgba(217,119,6,0.06), transparent)' }}
                  animate={{ x: ['-100%', '100%'] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                />
                <span className="text-sm font-black uppercase tracking-wider text-white relative z-10">Prendre contact</span>
                <ArrowRight size={14} className="text-amber-500 group-hover:translate-x-1 transition-transform relative z-10" />
              </button>

              <button
                onClick={() => navigate('/manifeste')}
                className="flex items-center justify-center gap-2 px-7 py-3.5 transition-all w-full sm:w-auto"
                style={{ border: '1px solid rgba(41,37,36,0.5)' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(82,82,82,0.6)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(41,37,36,0.5)'}
              >
                <span className="text-xs font-mono uppercase tracking-widest text-stone-200 hover:text-stone-200">Lire le manifeste</span>
                <ArrowUpRight size={12} className="text-stone-300" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <footer className="py-8 px-6 text-center text-xs text-white font-mono uppercase tracking-widest"
        style={{ borderTop: '1px solid rgba(217,119,6,0.05)', background: 'rgba(2,6,23,0.5)' }}>
        © 2026 · PESACORE Architect : Tous droits réservés.
      </footer>
    </div>
  );
}